import React, { useMemo, useState } from "react";
import { AlertCircle, Calendar, CheckCircle2, Lightbulb, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const TONE_PRIORITY = {
  high: "bg-red-50 text-red-700 border-red-200",
  normal: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-gray-100 text-gray-700 border-gray-200",
};

const SECTION_META = {
  followup_overdue: {
    title: "Follow-up overdue",
    Icon: AlertCircle,
    tone: "red",
    accent: "border-red-200 bg-red-50/40",
  },
  due_today: {
    title: "Due today",
    Icon: Calendar,
    tone: "amber",
    accent: "border-amber-200 bg-amber-50/40",
  },
  high_priority: {
    title: "High priority",
    Icon: AlertCircle,
    tone: "red",
    accent: "border-red-100 bg-white",
  },
  suggested: {
    title: "Suggested actions",
    Icon: Lightbulb,
    tone: "gray",
    accent: "border-gray-200 bg-white",
  },
};

const HeaderIcon = ({ Icon, tone }) => {
  const TONE = {
    red: "bg-red-50 text-red-700 border-red-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    gray: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return (
    <span className={`w-7 h-7 rounded-md flex items-center justify-center border ${TONE[tone]}`}>
      <Icon className="w-4 h-4" />
    </span>
  );
};

const ActionItem = ({ item, onAfterPatch, onJumpToUser }) => {
  const [busy, setBusy] = useState("");
  // `done` shows a green "Done" confirmation card before the parent refetch
  // removes this item from the queue. Provides a clear visual that the
  // click actually did something even if the refetch is fast.
  const [done, setDone] = useState(false);
  // `followupOffset` (in days) — shows a "Follow-up in Xd" badge briefly
  // after +1d/+3d are clicked, so the admin sees the action landed.
  const [followupOffset, setFollowupOffset] = useState(null);

  const callRetention = async (body, action) => {
    setBusy(action);
    try {
      await api.patch(`/admin/users/${item.user_id}/retention`, body);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Couldn't update ${action}.`);
      return false;
    } finally {
      setBusy("");
    }
  };

  const onMarkDone = async () => {
    // "Done" for the auto-queue = mark contacted today + clear follow-up
    // + record a per-type dismissal so the queue regenerator suppresses
    // this exact signal for ~14 days. Without admin_queue_dismissed_type
    // the same row would re-render on the next /admin refresh because
    // the underlying flag (trial_expired / no_leads_yet / …) is still true.
    const ok = await callRetention(
      {
        admin_last_contacted_at: new Date().toISOString(),
        admin_follow_up_at: null,
        admin_queue_dismissed_at: new Date().toISOString(),
        admin_queue_dismissed_type: item.type,
      },
      "mark-done",
    );
    if (!ok) return;
    // Show the "Done" success state, then trigger a parent refetch so this
    // card is removed from the queue. 900ms is long enough to register the
    // confirmation but short enough that the UI stays snappy.
    setDone(true);
    toast.success("Marked done — clearing from queue.");
    setTimeout(() => {
      onAfterPatch?.();
    }, 900);
  };
  const onFollowupOffset = async (days) => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(9, 0, 0, 0);
    const ok = await callRetention({ admin_follow_up_at: d.toISOString() }, "followup");
    if (!ok) return;
    setFollowupOffset(days);
    toast.success(
      days === 1 ? "Follow-up set for tomorrow." : `Follow-up set in ${days} days.`,
    );
    // Refresh the queue so this card is regrouped/cleared by the server.
    setTimeout(() => {
      onAfterPatch?.();
    }, 900);
  };
  const onOpenUser = () => {
    onJumpToUser?.(item.user_id);
  };

  // ---- Render: "Done" success card (replaces the row briefly) ----
  if (done) {
    return (
      <div
        className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-2 transition-all"
        data-testid={`action-item-${item.id}-done`}
      >
        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-emerald-900">Done</p>
          <p className="text-[12px] text-emerald-800/80 truncate">
            {item.email} · marked contacted today, follow-up cleared.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-3 flex items-start justify-between gap-3"
      data-testid={`action-item-${item.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${TONE_PRIORITY[item.priority] || TONE_PRIORITY.normal}`}
          >
            {item.priority}
          </span>
          <span className="font-medium text-gray-900 text-[14px] truncate">
            {item.title}
          </span>
          {followupOffset != null ? (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold"
              data-testid={`action-followup-pill-${item.id}`}
            >
              {followupOffset === 1
                ? "Follow-up tomorrow"
                : `Follow-up in ${followupOffset} days`}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 truncate">
          {item.email}
        </div>
        <div className="mt-1 text-[12px] text-gray-600">{item.reason}</div>
        <div className="mt-0.5 text-[12px] text-emerald-800/80 italic">
          → {item.suggested_action}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <button
          type="button"
          onClick={onMarkDone}
          disabled={!!busy}
          title="Remind again in 14 days"
          aria-label="Mark done — remind again in 14 days"
          className="text-[11px] font-semibold px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors min-w-[78px] text-center"
          data-testid={`action-mark-done-${item.id}`}
        >
          {busy === "mark-done" ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Mark done"}
        </button>
        {/* Reinforcement label right below the button — makes the 14-day
            reappear behavior discoverable without a hover (tooltips are
            invisible on mobile / touch). */}
        <span
          className="text-[10px] text-gray-400 leading-none"
          data-testid={`action-mark-done-hint-${item.id}`}
        >
          Remind again in 14 days
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => onFollowupOffset(1)}
            disabled={!!busy}
            className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 transition-colors"
            data-testid={`action-followup-1d-${item.id}`}
          >
            +1d
          </button>
          <button
            type="button"
            onClick={() => onFollowupOffset(3)}
            disabled={!!busy}
            className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-200 text-gray-600 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-800 transition-colors"
            data-testid={`action-followup-3d-${item.id}`}
          >
            +3d
          </button>
        </div>
        <button
          type="button"
          onClick={onOpenUser}
          className="text-[11px] text-emerald-800 hover:underline font-medium"
          data-testid={`action-open-user-${item.id}`}
        >
          Open user →
        </button>
      </div>
    </div>
  );
};

const Section = ({ kind, items, onAfterPatch, onJumpToUser }) => {
  const meta = SECTION_META[kind] || SECTION_META.suggested;
  if (!items.length) return null;
  return (
    <div className={`rounded-xl border p-3 ${meta.accent}`} data-testid={`action-section-${kind}`}>
      <div className="flex items-center gap-2 mb-2">
        <HeaderIcon Icon={meta.Icon} tone={meta.tone} />
        <h3 className="font-display text-sm font-semibold text-gray-900">
          {meta.title}
        </h3>
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-white border border-gray-200 text-[11px] font-bold text-gray-700"
          data-testid={`action-section-count-${kind}`}
        >
          {items.length}
        </span>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <ActionItem
            key={it.id}
            item={it}
            onAfterPatch={onAfterPatch}
            onJumpToUser={onJumpToUser}
          />
        ))}
      </div>
    </div>
  );
};

export const DailyActionQueue = ({ queue = [], loading, onAfterPatch, onJumpToUser }) => {
  const grouped = useMemo(() => {
    const g = { followup_overdue: [], due_today: [], high_priority: [], suggested: [] };
    for (const it of queue) {
      if (it.type === "followup_overdue") g.followup_overdue.push(it);
      else if (it.type === "due_today") g.due_today.push(it);
      else if (it.priority === "high") g.high_priority.push(it);
      else g.suggested.push(it);
    }
    return g;
  }, [queue]);

  const total = queue.length;

  return (
    <section
      className="mt-6 rounded-2xl bg-white border border-gray-200 overflow-hidden"
      data-testid="admin-action-queue"
    >
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          </span>
          <div>
            <h2 className="font-display text-lg font-semibold text-gray-900">
              Today&apos;s Action Queue
            </h2>
            <p className="text-xs text-gray-500">
              Auto-generated from customer signals. Mark items done to clear them.
            </p>
          </div>
        </div>
        <span
          className="inline-flex items-center justify-center min-w-[28px] h-[28px] px-2 rounded-full bg-emerald-700 text-white text-[12px] font-bold"
          data-testid="action-queue-total"
        >
          {total}
        </span>
      </header>
      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && total === 0 ? (
          <div className="md:col-span-2 text-center text-gray-500 py-6" data-testid="action-queue-loading">
            <Loader2 className="w-4 h-4 animate-spin inline" />
          </div>
        ) : total === 0 ? (
          <div
            className="md:col-span-2 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-5 py-10 text-center text-gray-500"
            data-testid="action-queue-empty"
          >
            Inbox zero. Nothing urgent right now.
          </div>
        ) : (
          <>
            <Section kind="followup_overdue" items={grouped.followup_overdue} onAfterPatch={onAfterPatch} onJumpToUser={onJumpToUser} />
            <Section kind="due_today" items={grouped.due_today} onAfterPatch={onAfterPatch} onJumpToUser={onJumpToUser} />
            <Section kind="high_priority" items={grouped.high_priority} onAfterPatch={onAfterPatch} onJumpToUser={onJumpToUser} />
            <Section kind="suggested" items={grouped.suggested} onAfterPatch={onAfterPatch} onJumpToUser={onJumpToUser} />
          </>
        )}
      </div>
    </section>
  );
};
